import React, { useState, useEffect, useCallback } from 'react';
import feedAlertsAdminAPI, { FeedAlert, FeedAlertCounts } from '../../../services/feedAlertsAdminAPI';
import './FeedAlertsAdmin.css';

const CATEGORY_LABELS: Record<string, string> = {
  shell_quality: 'Shell Quality',
  yolk_quality: 'Yolk Quality',
  feed_palatability: 'Feed Palatability',
  feed_quality_complaint: 'Feed Quality',
  feather_condition: 'Feather Condition',
  lethargy: 'Lethargy',
  mortality: 'Mortality',
  production_drop: 'Production Drop',
  competitor_feed: 'Competitor Feed',
  egg_size: 'Egg Size',
  leg_bone: 'Leg/Bone',
  digestive: 'Digestive',
};

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'viewed', label: 'Viewed' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'dismissed', label: 'Dismissed' },
];

const FeedAlertsAdmin: React.FC = () => {
  const [alerts, setAlerts] = useState<FeedAlert[]>([]);
  const [counts, setCounts] = useState<FeedAlertCounts>({ new: 0, high_priority: 0, total_unresolved: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState('');

  const loadAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (categoryFilter) params.category = categoryFilter;
      if (showAll) params.show_all = '1';
      const data = await feedAlertsAdminAPI.getAlerts(params);
      setAlerts(data);
    } catch (err) {
      console.error('Failed to load alerts:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, categoryFilter, showAll]);

  const loadCounts = useCallback(async () => {
    try {
      const data = await feedAlertsAdminAPI.getCounts();
      setCounts(data);
    } catch (err) {
      console.error('Failed to load counts:', err);
    }
  }, []);

  useEffect(() => {
    loadAlerts();
    loadCounts();
  }, [loadAlerts, loadCounts]);

  const handleStatusChange = async (alertId: string, newStatus: string) => {
    if (newStatus === 'resolved' || newStatus === 'dismissed') {
      setResolvingId(alertId);
      return;
    }
    try {
      await feedAlertsAdminAPI.updateAlert(alertId, { status: newStatus });
      loadAlerts();
      loadCounts();
    } catch (err) {
      console.error('Failed to update alert:', err);
    }
  };

  const handleResolve = async (alertId: string, status: string) => {
    try {
      await feedAlertsAdminAPI.updateAlert(alertId, {
        status,
        resolution_notes: resolutionNotes,
      });
      setResolvingId(null);
      setResolutionNotes('');
      loadAlerts();
      loadCounts();
    } catch (err) {
      console.error('Failed to resolve alert:', err);
    }
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const toggleFilter = (setter: React.Dispatch<React.SetStateAction<string>>, current: string, value: string) => {
    setter(current === value ? '' : value);
  };

  return (
    <div className="feed-alerts-admin">
      <h1>Feed Issue Alerts</h1>

      {/* Summary Cards */}
      <div className="feed-alerts-summary">
        <div className="summary-card new-alerts">
          <div className="count">{counts.new}</div>
          <div className="label">New Alerts</div>
        </div>
        <div className="summary-card high-priority">
          <div className="count">{counts.high_priority}</div>
          <div className="label">High Priority</div>
        </div>
        <div className="summary-card total-unresolved">
          <div className="count">{counts.total_unresolved}</div>
          <div className="label">Total Unresolved</div>
        </div>
      </div>

      {/* Filters */}
      <div className="feed-alerts-filters">
        <button
          className={`filter-chip ${priorityFilter === 'high' ? 'active' : ''}`}
          onClick={() => toggleFilter(setPriorityFilter, priorityFilter, 'high')}
        >
          High Priority
        </button>
        <button
          className={`filter-chip ${priorityFilter === 'medium' ? 'active' : ''}`}
          onClick={() => toggleFilter(setPriorityFilter, priorityFilter, 'medium')}
        >
          Medium Priority
        </button>
        <button
          className={`filter-chip ${categoryFilter === 'competitor_feed' ? 'active' : ''}`}
          onClick={() => toggleFilter(setCategoryFilter, categoryFilter, 'competitor_feed')}
        >
          Churn Risk
        </button>
        <button
          className={`filter-chip ${categoryFilter === 'mortality' ? 'active' : ''}`}
          onClick={() => toggleFilter(setCategoryFilter, categoryFilter, 'mortality')}
        >
          Mortality
        </button>
        <button
          className={`filter-chip ${statusFilter === 'new' ? 'active' : ''}`}
          onClick={() => toggleFilter(setStatusFilter, statusFilter, 'new')}
        >
          New Only
        </button>
        <button
          className={`filter-chip ${statusFilter === 'contacted' ? 'active' : ''}`}
          onClick={() => toggleFilter(setStatusFilter, statusFilter, 'contacted')}
        >
          Contacted
        </button>
        <button
          className={`filter-chip ${showAll ? 'active' : ''}`}
          onClick={() => setShowAll(!showAll)}
        >
          Show Resolved
        </button>
      </div>

      {/* Alert List */}
      {loading ? (
        <div className="feed-alerts-loading">Loading alerts...</div>
      ) : alerts.length === 0 ? (
        <div className="feed-alerts-empty">
          <p>No feed alerts found.</p>
          <p>Alerts are automatically created when farmer notes mention feed-related concerns.</p>
        </div>
      ) : (
        <div className="feed-alerts-list">
          {alerts.map((alert) => (
            <div key={alert.id} className={`feed-alert-card priority-${alert.priority} category-${alert.issue_category}`}>
              {alert.issue_category === 'competitor_feed' && (
                <div className="churn-banner">
                  CHURN RISK — Customer mentioning competitor feed. Call ASAP.
                </div>
              )}

              <div className="alert-header">
                <span className="alert-customer">{alert.customer_name}</span>
                <div className="alert-badges">
                  <span className={`priority-badge ${alert.priority}`}>{alert.priority}</span>
                  <span className="category-badge">{CATEGORY_LABELS[alert.issue_category] || alert.issue_category}</span>
                </div>
              </div>

              <div className="alert-contact">
                <a href={`tel:${alert.customer_phone}`}>{alert.customer_phone}</a>
                {alert.contact_person && <> &mdash; {alert.contact_person}</>}
                {alert.flock_name && <span className="alert-flock"> | Flock: {alert.flock_name}</span>}
              </div>

              <div className="alert-quote">
                <strong>Farmer said:</strong>
                <p style={{ margin: '4px 0 0' }}>"{alert.detected_text}"</p>
              </div>

              <div className="alert-keywords">
                {alert.matched_keywords.map((kw, i) => (
                  <span key={i} className="keyword-tag">{kw}</span>
                ))}
              </div>

              <div className="alert-details">
                <div className="alert-detail-section cause">
                  <strong>Likely Cause</strong>
                  <p>{alert.likely_cause}</p>
                </div>
                <div className="alert-detail-section action">
                  <strong>Suggested Action</strong>
                  <p>{alert.suggested_action}</p>
                </div>
              </div>

              <div className="alert-footer">
                <span className="alert-time">{formatDate(alert.created_at)} | {alert.source}</span>
                <div className="alert-actions">
                  <select
                    className="status-select"
                    value={alert.status}
                    onChange={(e) => handleStatusChange(alert.id, e.target.value)}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {resolvingId === alert.id && (
                <div className="resolution-form">
                  <textarea
                    placeholder="Resolution notes (optional)..."
                    value={resolutionNotes}
                    onChange={(e) => setResolutionNotes(e.target.value)}
                  />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      className="btn-resolve"
                      onClick={() => handleResolve(alert.id, 'resolved')}
                    >
                      Mark Resolved
                    </button>
                    <button
                      className="btn-resolve"
                      style={{ background: '#6c757d' }}
                      onClick={() => handleResolve(alert.id, 'dismissed')}
                    >
                      Dismiss
                    </button>
                    <button
                      className="btn-resolve"
                      style={{ background: 'transparent', color: '#666', border: '1px solid #ddd' }}
                      onClick={() => { setResolvingId(null); setResolutionNotes(''); }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {alert.resolution_notes && alert.status === 'resolved' && (
                <div style={{ marginTop: '8px', padding: '8px 12px', background: '#f0f0f0', borderRadius: '4px', fontSize: '0.85rem' }}>
                  <strong>Resolution:</strong> {alert.resolution_notes}
                  {alert.resolved_at && <span style={{ color: '#999', marginLeft: '8px' }}>({formatDate(alert.resolved_at)})</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FeedAlertsAdmin;
